import { useEffect, useRef, useState } from "react";
import { Head, router, useForm } from "@inertiajs/react";
import Tooltip from "bootstrap/js/dist/tooltip";
import AuthLayout from "@/layouts/auth-layout";
import { useConfirmDialog } from "@/components/confirm-dialog-provider";
import {
  manage_product_knowledge_file_path,
  manage_product_knowledge_files_path,
  manage_product_path,
  manage_products_path,
  product_knowledge_file_path,
  product_path,
  products_path
} from "@/routes";

function formatBytes(value) {
  if (!value) return "0 B";

  const units = ["B", "KB", "MB", "GB", "TB"];
  const index = Math.min(Math.floor(Math.log(value) / Math.log(1024)), units.length - 1);
  const size = value / (1024 ** index);

  return `${new Intl.NumberFormat("da-DK", { maximumFractionDigits: index === 0 ? 0 : 1 }).format(size)} ${units[index]}`;
}

function fileTypeLabel(file) {
  const filenameExtension = file.filename?.split(".").pop()?.toUpperCase();
  if (filenameExtension && filenameExtension !== file.filename?.toUpperCase()) return filenameExtension;

  return "Fil";
}

function OpenaiStatusIcon({ file }) {
  if (file.openai_status === "failed") {
    return (
      <i
        className="bi bi-exclamation-triangle-fill text-danger fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title="Der opstod en fejl under synkroniseringen. Slet filen og prøv igen."
        aria-label="Der opstod en fejl under synkroniseringen. Slet filen og prøv igen."
      />
    );
  }

  if (file.openai_status === "synced") {
    return (
      <i
        className="bi bi-check-circle-fill text-success fs-5"
        data-bs-toggle="tooltip"
        data-bs-placement="left"
        title="Synkroniseret til OpenAI."
        aria-label="Synkroniseret til OpenAI."
      />
    );
  }

  return (
    <span
      className="spinner-border spinner-border-sm text-secondary"
      data-bs-toggle="tooltip"
      data-bs-placement="left"
      role="status"
      title="Synkronisering igang."
      aria-label="Synkronisering igang."
    />
  );
}

export default function ProductKnowledgeFilesIndex({ product, knowledge_files: files = [], manage = false, embedded = false }) {
  const { confirm } = useConfirmDialog();
  const uploadForm = useForm({ product_knowledge_file: { files: [] } });
  const fileInputRef = useRef(null);
  const filesTableRef = useRef(null);
  const [editingFileId, setEditingFileId] = useState(null);
  const [editingFilename, setEditingFilename] = useState("");
  const [renameError, setRenameError] = useState("");
  const hasPendingFiles = manage && files.some((file) => file.openai_status === "pending");
  const downloadPath = (file) => manage ? manage_product_knowledge_file_path(product.id, file.id) : product_knowledge_file_path(product.id, file.id);

  useEffect(() => {
    if (!hasPendingFiles) return undefined;

    const timer = window.setInterval(() => {
      router.reload({
        only: ["knowledge_files"],
        preserveScroll: true
      });
    }, 3000);

    return () => window.clearInterval(timer);
  }, [hasPendingFiles]);

  useEffect(() => {
    if (!filesTableRef.current) return undefined;

    const tooltips = Array.from(filesTableRef.current.querySelectorAll('[data-bs-toggle="tooltip"]')).map((element) => new Tooltip(element));

    return () => tooltips.forEach((tooltip) => tooltip.dispose());
  }, [files]);

  const uploadFile = (event) => {
    event.preventDefault();
    if (!manage) return;

    uploadForm.post(manage_product_knowledge_files_path(product.id), {
      preserveScroll: true,
      forceFormData: true,
      onSuccess: () => {
        uploadForm.reset();
        if (fileInputRef.current) fileInputRef.current.value = "";
      }
    });
  };

  const startRenaming = (file) => {
    setEditingFileId(file.id);
    setEditingFilename(file.filename || file.name || "");
    setRenameError("");
  };

  const cancelRenaming = () => {
    setEditingFileId(null);
    setEditingFilename("");
    setRenameError("");
  };

  const submitRename = (event, file) => {
    event.preventDefault();
    if (!manage) return;

    const filename = editingFilename.trim();
    if (!filename) {
      setRenameError("Filnavn må ikke være tomt.");
      return;
    }

    router.patch(manage_product_knowledge_file_path(product.id, file.id), {
      product_knowledge_file: { filename }
    }, {
      preserveScroll: true,
      onSuccess: cancelRenaming
    });
  };

  const removeFile = async (file) => {
    if (!manage) return;

    const filename = file.filename || file.name;
    const confirmed = await confirm({
      title: "Fjern fil",
      description: `Vil du fjerne ${filename} fra ${product.name}?`,
      confirmLabel: "Fjern",
      confirmVariant: "danger"
    });

    if (!confirmed) return;

    router.delete(manage_product_knowledge_file_path(product.id, file.id), { preserveScroll: true });
  };

  return (
    <>
      {!embedded ? <Head title={`${product.name} produktviden`} /> : null}

      <section className={embedded ? "product-knowledge-files-embedded" : "workspace-content organization-files-page"}>
        <div className={embedded ? "product-knowledge-files-embedded__card" : "card"}>
          <div className={embedded ? "product-knowledge-files-embedded__body" : "card-body"}>
            <div className="workspace-section__eyebrow">Produktviden</div>
            <h2 className="workspace-section__title">Vidensdatabase</h2>
            <p className="workspace-admin-copy mb-0">
              Her finder du vejledning, referenceark og supplerende materiale til produktet.
            </p>

            {manage ? (
              <form className="row g-3 align-items-end mt-2" onSubmit={uploadFile}>
                <div className="col-12 col-lg-8">
                  <label className="form-label fw-semibold" htmlFor="product-knowledge-files">
                    Tilføj filer
                  </label>
                  <input
                    id="product-knowledge-files"
                    ref={fileInputRef}
                    className="form-control"
                    type="file"
                    multiple
                    onChange={(event) => uploadForm.setData("product_knowledge_file", { files: Array.from(event.target.files || []) })}
                    required
                  />
                </div>
                <div className="col-12 col-lg-4">
                  <button className="btn btn-primary w-100" disabled={uploadForm.processing || uploadForm.data.product_knowledge_file.files.length === 0}>
                    Upload filer
                  </button>
                </div>
              </form>
            ) : null}

            <div className="workspace-admin-table mt-4" ref={filesTableRef}>
              <div className="table-responsive">
                <table className="table align-middle mb-0 organization-files-table">
                  <colgroup>
                    <col className="organization-files-table__name-column" />
                    <col className="organization-files-table__type-column" />
                    <col className="organization-files-table__size-column" />
                    {manage ? <col className="organization-files-table__status-column" /> : null}
                    <col className={`organization-files-table__actions-column${manage ? " organization-files-table__actions-column--triple" : ""}`} />
                  </colgroup>
                  <thead>
                    <tr>
                      <th>Fil</th>
                      <th>Type</th>
                      <th>Størrelse</th>
                      {manage ? <th>AI-status</th> : null}
                      <th className="text-end">Handling</th>
                    </tr>
                  </thead>
                  <tbody>
                    {files.length === 0 ? (
                      <tr>
                        <td colSpan={manage ? 5 : 4} className="text-muted py-4">
                          Der er ingen produktvidensfiler endnu.
                        </td>
                      </tr>
                    ) : (
                      files.map((file) => (
                        <tr key={file.id}>
                          <td>
                            <div className="organization-files-table__file">
                              <span className="organization-files-table__icon">
                                <i className="bi bi-file-earmark" aria-hidden="true" />
                              </span>
                              {editingFileId === file.id ? (
                                <form className="organization-files-table__rename" onSubmit={(event) => submitRename(event, file)}>
                                  <input
                                    type="text"
                                    className="form-control form-control-sm"
                                    value={editingFilename}
                                    onChange={(event) => {
                                      setEditingFilename(event.target.value);
                                      setRenameError("");
                                    }}
                                  />
                                  <button type="submit" className="btn btn-sm btn-primary">
                                    Gem
                                  </button>
                                  <button type="button" className="btn btn-sm btn-outline-secondary" onClick={cancelRenaming}>
                                    Annuller
                                  </button>
                                  {renameError ? (
                                    <div className="organization-files-table__rename-error">{renameError}</div>
                                  ) : null}
                                </form>
                              ) : (
                                <span className="organization-files-table__name" title={file.filename || file.name}>{file.filename || file.name}</span>
                              )}
                            </div>
                          </td>
                          <td>
                            <span className="workspace-chip">{fileTypeLabel(file)}</span>
                          </td>
                          <td className="text-muted">{formatBytes(file.byte_size)}</td>
                          {manage ? <td><OpenaiStatusIcon file={file} /></td> : null}
                          <td className="text-end">
                            <div className="organization-files-table__actions">
                              <a
                                href={downloadPath(file)}
                                className="btn btn-outline-secondary organization-files-table__action"
                                aria-label={`Download ${file.filename || file.name}`}
                                title="Download"
                              >
                                <i className="bi bi-download" aria-hidden="true" />
                              </a>
                              {manage && editingFileId !== file.id ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-secondary organization-files-table__action"
                                  aria-label={`Omdøb ${file.filename || file.name}`}
                                  title="Omdøb"
                                  onClick={() => startRenaming(file)}
                                >
                                  <i className="bi bi-pencil" aria-hidden="true" />
                                </button>
                              ) : null}
                              {manage ? (
                                <button
                                  type="button"
                                  className="btn btn-outline-danger organization-files-table__action"
                                  aria-label={`Fjern ${file.filename || file.name}`}
                                  title="Fjern"
                                  onClick={() => removeFile(file)}
                                >
                                  <i className="bi bi-trash" aria-hidden="true" />
                                </button>
                              ) : null}
                            </div>
                          </td>
                        </tr>
                      ))
                    )}
                  </tbody>
                </table>
              </div>
            </div>
          </div>
        </div>
      </section>
    </>
  );
}

ProductKnowledgeFilesIndex.layout = (page) => {
  const { product, manage = false } = page.props;

  return (
    <AuthLayout
      header={{
        breadcrumbs: manage ? [
          { label: "Administration", href: manage_products_path() },
          { label: "Alle produkter", href: manage_products_path() },
          { label: product.name, href: `${manage_product_path(product.id)}/edit` },
          { label: "Produktviden" }
        ] : [
          { label: "Produkter", href: products_path() },
          { label: product.name, href: product_path(product.id) },
          { label: "Produktviden" }
        ],
        title: "Produktviden",
        actions: [
          {
            href: manage ? `${manage_product_path(product.id)}/edit` : product_path(product.id),
            label: "Tilbage",
            variant: "outline-secondary"
          }
        ]
      }}
    >
      {page}
    </AuthLayout>
  );
};
